import React from 'react';
import generateRewardCenterCardDataRendering from 'shared/screens/RewardCenter/utils/generateRewardCenterCardDataRendering';
import RewardCenterCardContainer from './RewardCenterCard';
import styled from 'styled-components';
import { sendRewardCenterEvent } from 'shared/screens/RewardCenter/analytics/rewardCenterlogger';
import { REWARD_CENTER_EVENT_TYPES } from 'shared/screens/RewardCenter/constants';
import { REWARD_CARD_LAYOUT } from 'shared/screens/RewardCenter/hooks/useRewardCardTypography';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../../utils/mobileSupported';

const L = REWARD_CARD_LAYOUT;
const useMobileWebCategoryLayout = isStandalonePWA || isNotStandaloneMobilePWA;
const mobileWebCategoryTitleBase = window.innerHeight <= 575 ? 0.9 : 0.95;

const RewardCenterCategories = ({
  object,
  rewardCenterAssets,
  setFilterData,
  setShowCategories,
  setCategorySelected,
  RewardsImageBaseURL,
}) => {
  const { Frame } = generateRewardCenterCardDataRendering(object, rewardCenterAssets);
  const { categoryId, categoryName, categoryDescription, categoryImage } = object;
  const categoryImageSrc = categoryImage && RewardsImageBaseURL ? `${RewardsImageBaseURL}${categoryImage}` : null;

  const handleClick = () => {
    setFilterData(categoryId);
    setCategorySelected(categoryId);
    setShowCategories(false);
    sendRewardCenterEvent(REWARD_CENTER_EVENT_TYPES.OPEN_REWARD_CENTER_CATEGORY, { category: object });
  };

  return (
    <RewardCenterCardContainer handleClick={handleClick} Frame={Frame}>
      <CardContent>
        {categoryImageSrc && <CardImage src={categoryImageSrc} />}
        <DescriptionWrapper>
          <TitleSlot>
            <Title>{categoryName}</Title>
          </TitleSlot>
          <SubtitleSlot>
            <Subtitle>{categoryDescription}</Subtitle>
          </SubtitleSlot>
        </DescriptionWrapper>
      </CardContent>
    </RewardCenterCardContainer>
  );
};

export default RewardCenterCategories;

const CardContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #ac7601;
  border-radius: 10%;
`;

const CardImage = styled.img`
  width: 100%;
  object-fit: contain;
  position: relative;
  border-top-left-radius: 10%;
  border-top-right-radius: 10%;
  height: 75%;
`;

// Fixed slot heights + top-aligned content keep title and subtitle baselines
// identical across every card in the row, regardless of how many lines each
// text actually occupies. Same structure as mobile (REWARD_CARD_LAYOUT).
const DescriptionWrapper = styled.div`
  --title-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.58rem, 0.92vw, 0.92rem)'};
  --sub-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.59rem, 0.92vw, 0.86rem)'};

  @media only screen and (min-height: 660px) {
    --title-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.69rem, 1.07vw, 1.1rem)'};
    --sub-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.7rem, 0.97vw, 1.03rem)'};
  }
  @media only screen and (min-height: 900px) {
    --title-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.81rem, 1.3vw, 1.15rem)'};
    --sub-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.81rem, 1.08vw, 1.13rem)'};
  }
  @media only screen and (min-height: 1000px) {
    --title-fs: ${useMobileWebCategoryLayout ? `${mobileWebCategoryTitleBase}rem` : 'clamp(0.88rem, 1.3vw, 1.26rem)'};
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 90%;
  z-index: 5;
  position: relative;
  flex: 4;
  padding: 0 0.25rem;
  margin-bottom: 1rem;

  @media only screen and (max-height: 650px) {
    margin-bottom: 0;
  }
`;

const TitleSlot = styled.div`
  width: 100%;
  height: calc(var(--title-fs) * ${L.titleLineHeightFactor} * ${L.titleLines});
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const SubtitleSlot = styled.div`
  width: 100%;
  height: calc(var(--sub-fs) * ${L.subtitleLineHeightFactor} * ${L.subtitleLines});
  margin-top: calc(var(--title-fs) * ${L.titleLineHeightFactor} * 0.18);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Title = styled.p`
  font-size: var(--title-fs);
  line-height: ${L.titleLineHeightFactor};
  text-transform: uppercase;
  color: #fff;
  text-align: center;
  font-weight: 700;
  margin: 0;
  width: 100%;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: ${L.titleLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Subtitle = styled.p`
  font-size: var(--sub-fs);
  line-height: ${L.subtitleLineHeightFactor};
  color: #fff;
  text-align: center;
  font-weight: 600;
  margin: 0;
  width: 100%;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: ${L.subtitleLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
